// CenteredLineLabel.ts
import { CanvasRenderingTarget2D } from 'fancy-canvas';
import {
  Coordinate,
  IChartApi,
  ISeriesApi,
  ISeriesPrimitive,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  Logical,
  SeriesOptionsMap,
  SeriesType,
  Time
} from 'lightweight-charts';

/* ===================== Types ===================== */

interface ViewPoint {
  x: Coordinate | null;
  y: Coordinate | null;
}

export interface CenteredLineLabelOptions {
  text: string;
  textColor: string;
  backgroundColor: string;
  font: string;
  padding: number;
  verticalOffset: number;
  borderRadius: number;
  horizontalOffset?: number;
  placeBelowLine?: boolean;

  /**
   * REQUIRED
   * Given a logical index, return the corresponding data point.
   * This keeps the primitive data-agnostic.
   */
  getPointAtLogical(logical: Logical): { time: Time; price: number } | null;
}

/* ===================== Defaults ===================== */

const defaultOptions: Omit<
  CenteredLineLabelOptions,
  'text' | 'getPointAtLogical'
> = {
  textColor: '#ffffff',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  font: '12px Arial',
  padding: 4,
  verticalOffset: 12,
  borderRadius: 4
};

/* ===================== Renderer ===================== */

class CenteredLineLabelPaneRenderer implements IPrimitivePaneRenderer {
  private _point: ViewPoint;
  private _options: CenteredLineLabelOptions;

  constructor(point: ViewPoint, options: CenteredLineLabelOptions) {
    this._point = point;
    this._options = options;
  }

  draw(target: CanvasRenderingTarget2D) {
    target.useBitmapCoordinateSpace(
      ({ context, horizontalPixelRatio, verticalPixelRatio }) => {
        if (this._point.x === null || this._point.y === null) return;

        const ctx = context;

        const x =
          Math.round(this._point.x * horizontalPixelRatio) +
          (this._options.horizontalOffset ?? 0);

        const y =
          Math.round(this._point.y * verticalPixelRatio) +
          (this._options.placeBelowLine
            ? this._options.verticalOffset
            : -this._options.verticalOffset);

        ctx.font = this._options.font;
        ctx.textBaseline = 'middle';

        const metrics = ctx.measureText(this._options.text);
        const textWidth = metrics.width;
        const textHeight = parseInt(this._options.font, 10);

        const padding = this._options.padding;
        const boxWidth = textWidth + padding * 2;
        const boxHeight = textHeight + padding * 2;

        const left = x - boxWidth / 2;
        const top = y - boxHeight / 2;
        const r = this._options.borderRadius;

        // Background
        ctx.fillStyle = this._options.backgroundColor;
        ctx.beginPath();
        ctx.moveTo(left + r, top);
        ctx.lineTo(left + boxWidth - r, top);
        ctx.quadraticCurveTo(left + boxWidth, top, left + boxWidth, top + r);
        ctx.lineTo(left + boxWidth, top + boxHeight - r);
        ctx.quadraticCurveTo(
          left + boxWidth,
          top + boxHeight,
          left + boxWidth - r,
          top + boxHeight
        );
        ctx.lineTo(left + r, top + boxHeight);
        ctx.quadraticCurveTo(left, top + boxHeight, left, top + boxHeight - r);
        ctx.lineTo(left, top + r);
        ctx.quadraticCurveTo(left, top, left + r, top);
        ctx.closePath();
        ctx.fill();

        // Text
        ctx.fillStyle = this._options.textColor;
        ctx.fillText(this._options.text, left + padding, y);
      }
    );
  }
}

/* ===================== Pane View ===================== */

class CenteredLineLabelPaneView implements IPrimitivePaneView {
  private _source: CenteredLineLabel;
  private _point: ViewPoint = { x: null, y: null };

  constructor(source: CenteredLineLabel) {
    this._source = source;
  }

  update() {
    const chart = this._source._chart;
    const series = this._source._series;
    const timeScale = chart.timeScale();

    const range = timeScale.getVisibleLogicalRange();
    if (!range) {
      this._point = { x: null, y: null };
      return;
    }

    const centerLogical = (range.from + range.to) / 2;

    const dataPoint = this._source._options.getPointAtLogical(
      toLogical(centerLogical)
    );

    if (!dataPoint) {
      this._point = { x: null, y: null };
      return;
    }

    this._point = {
      x: timeScale.timeToCoordinate(dataPoint.time),
      y: series.priceToCoordinate(dataPoint.price)
    };
  }

  renderer() {
    return new CenteredLineLabelPaneRenderer(
      this._point,
      this._source._options
    );
  }
}

/* ===================== Primitive ===================== */

export class CenteredLineLabel implements ISeriesPrimitive<Time> {
  _chart: IChartApi;
  _series: ISeriesApi<keyof SeriesOptionsMap>;
  _options: CenteredLineLabelOptions;
  _paneViews: CenteredLineLabelPaneView[];

  constructor(
    chart: IChartApi,
    series: ISeriesApi<SeriesType>,
    options: CenteredLineLabelOptions
  ) {
    this._chart = chart;
    this._series = series;
    this._options = { ...defaultOptions, ...options };
    this._paneViews = [new CenteredLineLabelPaneView(this)];
  }

  updateText(text: string) {
    this._options.text = text;
  }

  updateAllViews() {
    this._paneViews.forEach((v) => v.update());
  }

  paneViews() {
    return this._paneViews;
  }
}

function toLogical(value: number): Logical {
  return value as Logical;
}
